<template>
  <div class="promo-codes-list">
    <div v-if="loadingPromoCodes">
      <hs-table-skeleton :columns="7" :rows="10" />
    </div>

    <hs-empty-state
      v-else-if="localPromoCodes.length === 0"
      icon="tag"
      title="No promo codes yet"
      description="Create a promo code to start discounting sales for this store."
    />

    <div v-else class="promo-table-shell">
      <table class="promo-table">
        <thead>
          <tr>
            <th class="promo-table__select">
              <hs-checkbox :model-value="allSelected" @update:modelValue="toggleSelectAll" />
            </th>
            <th>Code</th>
            <th>Discount</th>
            <th>Minimum</th>
            <th>Usage</th>
            <th>Expiration</th>
            <th>Status</th>
            <th class="promo-table__actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in localPromoCodes" :key="item.id" @click="handleChange(item)">
            <td class="promo-table__select">
              <hs-checkbox
                :model-value="item.checked"
                @click.stop
                @update:modelValue="handleCheckboxChange(item)"
              />
            </td>
            <td>
              <div class="promo-code-cell">
                <span class="promo-code-cell__mark">
                  <hs-icon name="tag" :size="15" />
                </span>
                <div>
                  <strong>{{ item.code }}</strong>
                  <small>{{ item.is_active ? "Available for checkout" : "Hidden from checkout" }}</small>
                </div>
              </div>
            </td>
            <td>
              <div class="promo-discount">
                {{ discountLabel(item) }}
                <span>OFF</span>
              </div>
            </td>
            <td>{{ currencyLabel(item.minimum_purchase_required) }}</td>
            <td>
              <div class="promo-usage">
                <span>{{ item.usage_count || 0 }} / {{ item.usage_limit || "∞" }}</span>
                <div class="promo-usage__bar">
                  <i :style="{ width: `${usagePercent(item)}%` }" />
                </div>
              </div>
            </td>
            <td>{{ formatDate(item.expiration_date, false) }}</td>
            <td>
              <hs-badge :tone="getStatusTone(item)" size="sm">
                {{ getStatus(item) }}
              </hs-badge>
            </td>
            <td class="promo-table__actions" @click.stop>
              <hs-button variant="secondary" icon="edit-2" size="sm" @click="openUpdateModal(item)">
                Edit
              </hs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <UpdatePromoCode
      v-if="showUpdateModal"
      :selectedPromoCode="selectedPromoCode"
      @close-modal="handleModalClose"
      @promo-code-updated="handlePromoCodeUpdated"
    />
  </div>
</template>

<script>
import { formatNumber } from "@/helper/utils";
import UpdatePromoCode from "@/components/modals/promo_code/UpdatePromoCode.vue";
import { HsBadge, HsButton, HsCheckbox, HsEmptyState, HsIcon } from "@/components/ui";

export default {
  name: "PromoCodes",
  components: {
    HsBadge,
    HsButton,
    HsCheckbox,
    HsEmptyState,
    HsIcon,
    UpdatePromoCode,
  },
  props: {
    promoCodes: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  },
  emits: ["promo-codes-selected", "promo-code-updated"],
  data() {
    return {
      showUpdateModal: false,
      selectedPromoCode: null,
      localPromoCodes: [],
    };
  },
  computed: {
    loadingPromoCodes() {
      return this.isLoading || this.$store.getters.isLoadingPromoCodes;
    },
    allSelected() {
      return (
        this.localPromoCodes.length > 0 &&
        this.localPromoCodes.every((item) => item.checked)
      );
    },
  },
  watch: {
    promoCodes: {
      handler(newVal) {
        this.localPromoCodes = (newVal || []).map((item) => ({
          ...item,
          checked:
            this.localPromoCodes.find((promo) => promo.id === item.id)
              ?.checked || false,
        }));
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getFormattedValue(value) {
      return formatNumber(value || 0);
    },
    currencyLabel(value) {
      return `RF ${this.getFormattedValue(value)}`;
    },
    discountLabel(item) {
      if (item.discount_percentage) {
        return `${this.getFormattedValue(item.discount_percentage)}%`;
      }
      return this.currencyLabel(item.discount_amount);
    },
    usagePercent(item) {
      if (!item.usage_limit) return 0;
      return Math.min(100, Math.round(((item.usage_count || 0) / item.usage_limit) * 100));
    },
    formatDate(dateString, applyTimezone = true, timezoneOffset = 2) {
      const date = new Date(dateString);
      if (applyTimezone) {
        date.setTime(date.getTime() + timezoneOffset * 60 * 60 * 1000);
      }
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      });
    },
    emitSelection() {
      this.$emit(
        "promo-codes-selected",
        this.localPromoCodes.filter((promo) => promo.checked),
      );
    },
    handleChange(item) {
      const index = this.localPromoCodes.findIndex((promo) => promo.id === item.id);
      if (index !== -1) {
        this.localPromoCodes[index].checked = !this.localPromoCodes[index].checked;
      }
      this.emitSelection();
    },
    handleCheckboxChange(item) {
      this.handleChange(item);
    },
    toggleSelectAll() {
      const newState = !this.allSelected;
      this.localPromoCodes.forEach((item, index) => {
        this.localPromoCodes[index].checked = newState;
      });
      this.emitSelection();
    },
    openUpdateModal(item) {
      this.selectedPromoCode = item;
      this.showUpdateModal = true;
    },
    handleModalClose() {
      this.showUpdateModal = false;
      this.selectedPromoCode = null;
    },
    getStatus(item) {
      if (!item.is_active) return "Inactive";
      if (new Date(item.expiration_date) < new Date()) return "Expired";
      if (item.usage_limit && (item.usage_count || 0) >= item.usage_limit) {
        return "Depleted";
      }
      return "Active";
    },
    getStatusTone(item) {
      const tones = {
        Active: "success",
        Inactive: "neutral",
        Expired: "danger",
        Depleted: "warning",
      };
      return tones[this.getStatus(item)] || "neutral";
    },
    handlePromoCodeUpdated() {
      this.$emit("promo-code-updated");
      this.handleModalClose();
    },
  },
};
</script>

<style scoped>
.promo-codes-list {
  display: grid;
  gap: 1rem;
}

.promo-table-shell {
  background: color-mix(in srgb, var(--hs-surface) 94%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 18px;
  box-shadow: var(--hs-shadow-soft);
  overflow-x: auto;
}

.promo-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
  width: 100%;
}

.promo-table th {
  background: color-mix(in srgb, var(--hs-bg-soft) 74%, transparent);
  border-bottom: 1px solid var(--hs-border);
  color: var(--hs-muted);
  font-size: 0.68rem;
  font-weight: 950;
  letter-spacing: 0;
  padding: 0.9rem 1rem;
  text-align: left;
  text-transform: uppercase;
}

.promo-table td {
  border-bottom: 1px solid color-mix(in srgb, var(--hs-border) 74%, transparent);
  color: var(--hs-text);
  font-size: 0.86rem;
  font-weight: 780;
  padding: 0.9rem 1rem;
  vertical-align: middle;
}

.promo-table tbody tr {
  cursor: pointer;
  transition: background 0.18s ease;
}

.promo-table tbody tr:hover {
  background: color-mix(in srgb, var(--hs-primary) 5%, transparent);
}

.promo-table tbody tr:last-child td {
  border-bottom: 0;
}

.promo-table__select {
  width: 3.5rem;
}

.promo-table__actions {
  text-align: right;
  width: 8rem;
}

.promo-code-cell {
  align-items: center;
  display: flex;
  gap: 0.7rem;
}

.promo-code-cell__mark {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary) 12%, transparent);
  border-radius: 12px;
  color: var(--hs-primary);
  display: inline-flex;
  height: 2.2rem;
  justify-content: center;
  width: 2.2rem;
}

.promo-code-cell strong,
.promo-code-cell small {
  display: block;
}

.promo-code-cell strong {
  font-size: 0.92rem;
  font-weight: 950;
}

.promo-code-cell small {
  color: var(--hs-muted);
  font-size: 0.72rem;
  font-weight: 750;
  margin-top: 0.15rem;
}

.promo-discount {
  align-items: baseline;
  display: flex;
  font-weight: 950;
  gap: 0.35rem;
}

.promo-discount span {
  color: var(--hs-muted);
  font-size: 0.68rem;
}

.promo-usage {
  display: grid;
  gap: 0.35rem;
  min-width: 7rem;
}

.promo-usage__bar {
  background: color-mix(in srgb, var(--hs-muted) 12%, transparent);
  border-radius: 999px;
  height: 0.38rem;
  overflow: hidden;
}

.promo-usage__bar i {
  background: linear-gradient(90deg, var(--hs-primary), #22c55e);
  border-radius: inherit;
  display: block;
  height: 100%;
}
</style>
